import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { clarityEvent, gaEvent } from '../analytics';
import styles from './Navbar.module.css';

const treatmentLabels = {
  a: 'A · Current structure',
  b: 'B · Complete first screen',
  c: 'C · Core challenge first',
  d: 'D · Product proof first',
  e1: 'E1 · Decisions autoplay first',
  e2: 'E2 · Product autoplay first',
  e3: 'E3 · Difference autoplay first',
};

export default function Navbar({ experimentQa = null }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setIsScrolled(window.scrollY > 8);
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const handleNavClick = (e, target, label) => {
    e.preventDefault();
    closeMenu();

    gaEvent('click_nav_link', { nav_item: label });
    clarityEvent('click_nav_link');

    if (target === 'home') {
      if (location.pathname === '/') {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        window.history.replaceState(null, '', '/');
        return;
      }
      navigate('/');
      return;
    }

    if (target === 'inceptum') {
      navigate('/inceptum');
      return;
    }

    if (location.pathname === '/') {
      const el = document.getElementById(target);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
        window.history.replaceState(null, '', `/#${target}`);
      }
      return;
    }

    navigate(`/#${target}`);
  };

  const isStudioHome = location.pathname === '/';

  return (
    <>
      <nav
        className={`${styles.navbar} ${styles.navbarStudio} ${isScrolled ? styles.navbarStudioScrolled : ''}`}
      >
        <div className={styles.container}>
          <Link to="/" className={styles.logo} aria-label="StratMech Studios home">
            <img
              src="/images/webp/logo.webp"
              srcSet="/images/webp/logo_192w.webp 192w, /images/webp/logo.webp 357w"
              sizes="96px"
              alt="StratMech Studios"
              className={styles.logoImage}
              width={96}
              height={66}
            />
          </Link>
          <ul className={`${styles.navLinks} ${isMenuOpen ? styles.navLinksOpen : ''}`}>
            <li><a href="/" onClick={(e) => handleNavClick(e, 'home', 'Home')}>Home</a></li>
            <li><a href="/inceptum" onClick={(e) => handleNavClick(e, 'inceptum', 'INCEPTUM')}>INCEPTUM</a></li>
            <li>
              <a
                href="/about-us"
                onClick={(e) => { e.preventDefault(); closeMenu(); gaEvent('click_nav_link', { nav_item: 'About' }); clarityEvent('click_nav_link'); navigate('/about-us'); }}
              >
                About
              </a>
            </li>
            <li>
              <a
                href="/journal"
                onClick={(e) => { e.preventDefault(); closeMenu(); gaEvent('click_nav_link', { nav_item: 'Journal' }); clarityEvent('click_nav_link'); navigate('/journal'); }}
              >
                Journal
              </a>
            </li>
            <li><a href="/#partner" onClick={(e) => handleNavClick(e, 'partner', 'Partner')}>Partner</a></li>
            <li><a href="/contact" onClick={(e) => { e.preventDefault(); closeMenu(); gaEvent('click_nav_link', { nav_item: 'Contact' }); clarityEvent('click_nav_link'); navigate('/contact'); }}>Contact</a></li>
            {experimentQa?.enabled && (
              <li className={styles.qaMenuItem}>
                <label className={styles.qaMenuLabel} htmlFor="ig-experiment-treatment">
                  Experiment preview
                </label>
                <select
                  id="ig-experiment-treatment"
                  className={styles.qaMenuSelect}
                  value={experimentQa.treatment}
                  onChange={(event) => experimentQa.onChange(event.target.value)}
                >
                  {experimentQa.treatments.map((treatment) => (
                    <option key={treatment} value={treatment}>
                      {treatmentLabels[treatment]}
                    </option>
                  ))}
                </select>
              </li>
            )}
          </ul>
          <div className={styles.controls}>
            <button
              className={styles.hamburger}
              onClick={toggleMenu}
              aria-label="Toggle menu"
              aria-expanded={isMenuOpen}
            >
              <span className={styles.hamburgerLine}></span>
              <span className={styles.hamburgerLine}></span>
              <span className={styles.hamburgerLine}></span>
            </button>
          </div>
        </div>
      </nav>
      <div
        className={`${styles.menuBackdrop} ${isMenuOpen ? styles.menuBackdropVisible : ''}`}
        onClick={closeMenu}
        aria-hidden="true"
      />
    </>
  );
}
